import type { CreateCategoryDto } from "@/dtos/CreateCategoryDto";
import type { CreateSubCategoryDto } from "@/dtos/CreateSubCategoryDto";
import type { UpdateCategoryDto } from "@/dtos/UpdateCategoryDto";
import type { UpdateSubCategoryDto } from "@/dtos/UpdateSubCategoryDto";
import api from "@/services/api";
import { useMutation, useQuery } from "@tanstack/react-query";

export const useGetCategories = () => {
  return useQuery({
    queryKey: ["getCategories"],
    queryFn: async () => {
      const response = await api.get("/categories", {
        params: { limit: 100 },
      });
      return response.data;
    },
    staleTime: 1000 * 60 * 10, // 10 minutos - considera os dados frescos por 10 minutos
    gcTime: 1000 * 60 * 30, // 30 minutos - mantém no cache por 30 minutos
    refetchOnWindowFocus: false, // Não refaz a requisição ao focar na janela
    refetchOnMount: false, // Não refaz a requisição ao montar o componente se já tem cache
  });
};

export const useCreateCategory = () => {
  return useMutation({
    mutationKey: ["create-category"],
    mutationFn: async (createCategoryDto: CreateCategoryDto) => {
      const response = await api.post("/categories", createCategoryDto);

      return response.data;
    },
  });
};

export const useCreateSubCategory = () => {
  return useMutation({
    mutationKey: ["create-subcategory"],
    mutationFn: async (createSubCategoryDto: CreateSubCategoryDto) => {
      const response = await api.post(
        "/categories/subcategories",
        createSubCategoryDto,
      );

      return response.data;
    },
  });
};

export const useDeleteCategory = () => {
  return useMutation({
    mutationKey: ["delete-category"],
    mutationFn: async (categoryId: string) => {
      const response = await api.delete(`/categories/${categoryId}`);
      return response.data;
    },
  });
};

export const useDeleteSubCategory = () => {
  return useMutation({
    mutationKey: ["delete-subcategory"],
    mutationFn: async (subcategoryId: string) => {
      const response = await api.delete(
        `/categories/subcategories/${subcategoryId}`,
      );
      return response.data;
    },
  });
};

export const useUpdateSubCategory = () => {
  return useMutation({
    mutationKey: ["update-subcategory"],
    mutationFn: async (updateSubCategoryDto: UpdateSubCategoryDto) => {
      const response = await api.patch(
        `/categories/subcategories/${updateSubCategoryDto.id}`,
        {
          name: updateSubCategoryDto.name,
          description: updateSubCategoryDto.description,
        },
      );
      return response.data;
    },
  });
};

export const useUpdateCategory = () => {
  return useMutation({
    mutationKey: ["update-category"],
    mutationFn: async (updateCategoryDto: UpdateCategoryDto) => {
      const response = await api.patch(`/categories/${updateCategoryDto.id}`, {
        name: updateCategoryDto.name,
        description: updateCategoryDto.description,
        icon: updateCategoryDto.icon,
        lib: updateCategoryDto.lib,
        isActive: updateCategoryDto.isActive,
      });

      return response.data;
    },
  });
};
